import $http from "../../axios";
import { Notification } from 'element-ui';
import { filtersParam } from '../../components/dashboard-sale-indicators/filters';
import { loadCachedDashboard, saveCachedDashboard } from '../../components/dashboard-sale-indicators/dashboardCache';

// Gráficas de la carga completa (sin filtros): al quitar todos los filtros se restauran de aquí, sin
// volver a llamar al backend. Las que no responden a los filtros no cambian nunca, así que se puede
// fusionar el objeto completo.
let unfilteredCharts = null;

// Respuestas de gráficas por combinación de filtros (llave = filtersParam()): repetir una combinación
// ya vista no vuelve a consultar. Se vacía en cada carga completa (los datos pudieron cambiar).
let filteredChartsCache = {};

// Cada consulta de filtros toma un número; solo la ÚLTIMA puede pintar el resultado y apagar el
// "cargando". Sin esto, dos clicks rápidos dejaban que la respuesta lenta del primero llegara después
// de la del segundo y pisara el resultado correcto.
let filterRequestSeq = 0;

function notifyError(error) {
    Notification.error({
        title: 'Error!',
        message: error.message,
        type: 'error',
        duration: 1500,
        customClass: 'notification-box',
    });
}

// Carga completa (pantalla inicial y botón "Actualizar"): oculta todo el contenido con BrandedLoader
// y reemplaza todo el dashboard (kpis + charts) con la respuesta del backend. El backend cachea en
// JSON por rango de fecha (DashboardSaleIndicatorsService::getSalesDataset()): la carga inicial usa
// ese cache si existe (rápido), pero "Actualizar" (force=true, ver el botón en index.vue) siempre
// fuerza la consulta SQL para que el usuario pueda pedir datos frescos a demanda. Siempre resetea los
// filtros activos: con filtros el backend solo devuelve `charts` (sin `kpis`, ver
// DashboardSaleIndicatorsService::getDashboard()), así que mandarlos junto con una carga completa
// dejaría el dashboard sin kpis.
export async function fetchDashboard({ commit, state }, force = false) {
    commit('clearFilters');
    commit('setLoading', true);

    await requestDashboard({ commit, state }, force)
        .finally(() => commit('setLoading', false));
}

// Pide el dashboard completo, lo deja en el store y guarda la copia local (dashboardCache.js). Compartido
// por la carga completa (fetchDashboard) y por la actualización en segundo plano (initDashboard).
function requestDashboard({ commit, state }, force) {
    const params = { ...state.params };

    return $http.get('/api/dashboard-sale-indicators', { params: { ...params, force: !!force } })
        .then((response) => {
            commit('setDashboard', response.data.data);
            unfilteredCharts = response.data.data.charts;
            filteredChartsCache = {};
            saveCachedDashboard(params, response.data.data);
        })
        .catch(notifyError);
}

// Carga INICIAL del dashboard (al entrar después del login) — lo único que usa la copia local: si hay
// una guardada para el usuario y el rango, se pintan al instante sus KPIs/gráficas (con el aviso "Actualizando información"
// junto a "Ajustar Orden", state.refreshing) y se piden los datos frescos por detrás; si no hay copia
// (primera vez) es exactamente la carga de siempre (fetchDashboard, con BrandedLoader a pantalla completa).
export async function initDashboard({ commit, state, dispatch }) {
    const cached = loadCachedDashboard(state.params);

    if (!cached) {
        await dispatch('fetchDashboard');
        return;
    }

    commit('clearFilters');
    commit('setDashboard', cached);
    unfilteredCharts = cached.charts;
    filteredChartsCache = {};
    commit('setRefreshing', true);

    await requestDashboard({ commit, state }, false)
        .finally(() => commit('setRefreshing', false));
}

// Recarga de las gráficas filtrables según los filtros activos: el backend, al ver `filters`, SOLO
// recalcula y devuelve las gráficas de DashboardSaleIndicatorsService::FILTERABLE_CHARTS (sin kpis,
// mucho más rápido) — por eso aquí se fusiona la respuesta sobre dashboard.charts (mutation
// mergeCharts) en vez de reemplazar todo el dashboard, y no se oculta el contenido completo
// (state.filterLoading, no state.loading): solo esos charts quedan en su propio estado de carga.
async function fetchFilteredCharts({ commit, state }) {
    const requestSeq = ++filterRequestSeq;

    // Sin filtros: se restaura lo de la carga completa, sin llamar al backend.
    if (!state.filters.length) {
        commit('setFilterLoading', false);

        if (unfilteredCharts) {
            commit('mergeCharts', unfilteredCharts);
        }

        return;
    }

    const cacheKey = filtersParam(state.filters);

    if (filteredChartsCache[cacheKey]) {
        commit('setFilterLoading', false);
        commit('mergeCharts', filteredChartsCache[cacheKey]);
        return;
    }

    commit('setFilterLoading', true);

    await $http.get('/api/dashboard-sale-indicators', { params: { ...state.params, filters: cacheKey } })
        .then((response) => {
            const charts = response.data.data.charts;

            filteredChartsCache[cacheKey] = charts;

            if (requestSeq === filterRequestSeq) {
                commit('mergeCharts', charts);
            }
        })
        .catch(notifyError)
        .finally(() => {
            if (requestSeq === filterRequestSeq) {
                commit('setFilterLoading', false);
            }
        });
}

// Agrega un filtro: reemplaza el del mismo tipo (si lo hay) y se combina con los demás (AND). Un
// click sobre el mismo valor ya activo no hace nada.
export async function applyFilter({ commit, state }, filter) {
    if (filter.id === null || filter.id === undefined) {
        return;
    }

    const current = state.filters.find(f => f.type === filter.type);

    if (current && current.id === filter.id) {
        return;
    }

    commit('setFilters', state.filters
        .filter(f => f.type !== filter.type)
        .concat([{ type: filter.type, id: filter.id, label: filter.label }]));

    await fetchFilteredCharts({ commit, state });
}

// Quita solo el filtro de un tipo (la ✕ de su badge) y deja los demás.
export async function removeFilter({ commit, state }, type) {
    commit('setFilters', state.filters.filter(f => f.type !== type));
    await fetchFilteredCharts({ commit, state });
}

// Quita todos los filtros ("Quitar filtros").
export async function clearFilter({ commit, state }) {
    commit('clearFilters');
    await fetchFilteredCharts({ commit, state });
}



// WEBPACK FOOTER //
// ./src/modules/dashboard-sale-indicators/actions.js